import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import client, { setAccessToken, setOnAuthLost, refreshSession } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await client.get("/auth/me");
            setUser(data.user);
        } catch { setUser(null); }
    }, []);

    useEffect(() => {
        // A refresh that fails mid-session must clear the UI, not leave a stale user.
        setOnAuthLost(() => setUser(null));
    }, []);

    useEffect(() => {
        // Restore the session from the httpOnly refresh cookie so a page reload
        // doesn't log you out. Rotating, so each reload burns and reissues one.
        (async () => {
            try {
                const { data } = await refreshSession();
                setAccessToken(data.access_token);
                setUser(data.user);
            } catch {
                setAccessToken("");
                setUser(null);
            } finally {
                setReady(true);
            }
        })();
    }, []);

    const login = async (identifier, password) => {
        const { data } = await client.post("/auth/login", { identifier, password });
        setAccessToken(data.access_token);
        setUser(data.user);
        return data;
    };

    // `email` carries the address a phone signup must also give us — one-time
    // codes are delivered by email only.
    const signup = async (identifier, password, name, email) => {
        const { data } = await client.post("/auth/signup", { identifier, password, name, email });
        return data;
    };

    const verify = async (identifier, otp) => {
        const { data } = await client.post("/auth/verify", { identifier, otp });
        setAccessToken(data.access_token);
        setUser(data.user);
        return data;
    };

    const googleSignIn = async (credential) => {
        const { data } = await client.post("/auth/google", { credential });
        setAccessToken(data.access_token);
        setUser(data.user);
        return data;
    };

    const resendOtp = async (identifier) => {
        const { data } = await client.post("/auth/resend", { identifier });
        return data;
    };

    // Password recovery. Deliberately says nothing about whether the account
    // exists — the server answers identically either way.
    const forgotPassword = async (identifier) => {
        const { data } = await client.post("/auth/forgot", { identifier });
        return data;
    };

    const resetPassword = async (identifier, otp, newPassword) => {
        const { data } = await client.post("/auth/reset",
            { identifier, otp, new_password: newPassword });
        return data;
    };

    const logout = async () => {
        await client.post("/auth/logout").catch(() => {});
        setAccessToken("");
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, ready, login, signup, verify, logout, refreshMe, googleSignIn,
                                    resendOtp, forgotPassword, resetPassword }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
